import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type PersonaType = 'general' | 'security' | 'student' | 'creator' | 'business';

export interface PersonaInfo {
  id: PersonaType;
  name: string;
  description: string;
  icon: string;
  color: string;
  dashboardOrder: string[];
  samplePrompts: string[];
}

export const PERSONAS: Record<PersonaType, PersonaInfo> = {
  general: {
    id: 'general',
    name: 'General',
    description: 'Explore all features equally',
    icon: 'Sparkles',
    color: 'text-primary',
    dashboardOrder: ['chat', 'tools', 'pipelines', 'storybook', 'contracts'],
    samplePrompts: [
      'What can you help me with today?',
      'Show me your best features',
      'Help me get started',
    ],
  },
  security: {
    id: 'security',
    name: 'Security Professional',
    description: 'Contract analysis, risk assessment, CISO tools',
    icon: 'Shield',
    color: 'text-red-500',
    dashboardOrder: ['contracts', 'chat', 'pipelines', 'tools', 'storybook'],
    samplePrompts: [
      'Analyze this contract for risk',
      'What are common security vulnerabilities?',
      'Help me write a security policy',
    ],
  },
  student: {
    id: 'student',
    name: 'Student',
    description: 'Study tools, flashcards, academic writing',
    icon: 'GraduationCap',
    color: 'text-blue-500',
    dashboardOrder: ['college-ready', 'chat', 'storybook', 'tools', 'pipelines'],
    samplePrompts: [
      'Help me study for my exam',
      'Create flashcards for this topic',
      'Explain this concept simply',
    ],
  },
  creator: {
    id: 'creator',
    name: 'Content Creator',
    description: 'AI writing, image generation, humanization',
    icon: 'Palette',
    color: 'text-purple-500',
    dashboardOrder: ['storybook', 'chat', 'tools', 'pipelines', 'contracts'],
    samplePrompts: [
      'Write a blog post about...',
      'Humanize this AI-generated text',
      'Generate ideas for my content',
    ],
  },
  business: {
    id: 'business',
    name: 'Business Owner',
    description: 'Custom chatbots, automation, workflows',
    icon: 'Briefcase',
    color: 'text-green-500',
    dashboardOrder: ['pipelines', 'chatbot-builder', 'contracts', 'chat', 'tools'],
    samplePrompts: [
      'Create a chatbot for my website',
      'Build a workflow for customer support',
      'Analyze this business contract',
    ],
  },
};

export function usePersona() {
  const { user } = useAuth();
  const [persona, setPersonaState] = useState<PersonaType>('general');
  const [isLoading, setIsLoading] = useState(true);
  const [showSelector, setShowSelector] = useState(false);

  // Load persona from database
  useEffect(() => {
    const loadPersona = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('primary_persona')
          .eq('id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading persona:', error);
        }

        if (data?.primary_persona) {
          setPersonaState(data.primary_persona as PersonaType);
        } else {
          // First time user - show selector
          setShowSelector(true);
        }
      } catch (error) {
        console.error('Error loading persona:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPersona();
  }, [user]);

  // Update persona in database
  const setPersona = useCallback(async (newPersona: PersonaType) => {
    if (!user) return;

    setPersonaState(newPersona);

    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          primary_persona: newPersona,
        }, { onConflict: 'id' });

      if (error) throw error;
      
      toast.success(`Persona updated to ${PERSONAS[newPersona].name}`);
      setShowSelector(false);
    } catch (error) {
      console.error('Error updating persona:', error);
      toast.error('Failed to update persona');
    }
  }, [user]);

  // Get current persona info
  const currentPersona = PERSONAS[persona];

  // Get ordered dashboard items based on persona
  const getDashboardOrder = useCallback(() => {
    return currentPersona.dashboardOrder;
  }, [currentPersona]);

  // Get sample prompts for current persona
  const getSamplePrompts = useCallback(() => {
    return currentPersona.samplePrompts;
  }, [currentPersona]);

  return {
    persona,
    personaInfo: currentPersona,
    setPersona,
    isLoading,
    showSelector,
    setShowSelector,
    getDashboardOrder,
    getSamplePrompts,
    allPersonas: PERSONAS,
  };
}
